import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import Highcharts from 'highcharts/esm/highstock';
import { BackendError } from '../../../domain/error.domain';
import { SecurityService } from '../../../services/security.service';

import { DetailedQuote } from '../../../domain/quote.domain';
import { MarketType } from '../../../domain/market.domain';
import { SecuritySymbol, SymbolType } from '../../../domain/symbol.domain';
import { TradeInfo } from '../../../domain/buy-sell.domain';
import {
  DisplayTimeSeries,
  QuoteIssueType,
  QuoteTemplate,
  QuoteTimeRange,
  QuoteTimeSeries,
  QuoteTimeSeriesType
} from './domain';
import { QuoteChartConfigService } from './quote-charts/quote-chart-config.service';
import { QuoteParams, QuoteRestService, QuoteTimeRangeParams } from './quote-rest.service';
import { QuoteChartType, QuoteConstants, QuoteError } from './quote.constants';
import type { SeriesOptionsType } from 'highcharts';

@Component({
  template: ''
})
export abstract class QuoteComponent implements AfterViewInit, OnChanges, OnDestroy {

  @Input() quote: DetailedQuote;
  @Input() symbol: SecuritySymbol;
  @Input() accountId: string;
  @Input() quantity: number;
  @Input() inactivateBuySell: boolean;
  @Input() isOverlayOpen: boolean;
  @Input() displaySymbolDetail: boolean;
  @Output() readonly hideButtons: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() readonly tradeInfoChange: EventEmitter<TradeInfo> = new EventEmitter<TradeInfo>();
  @Output() readonly onQuoteUpdate: EventEmitter<DetailedQuote> = new EventEmitter<DetailedQuote>(true);
  @ViewChild('fixedIncome', { static: true }) templateRefFixedIncome: TemplateRef<unknown>;
  @ViewChild('message', { static: true }) templateRefMessage: TemplateRef<unknown>;
  @ViewChild('invalidSymbol', { static: true }) templateRefInvalidSymbol: TemplateRef<unknown>;

  // Quote Template string to show
  template: QuoteTemplate;

  // Quote Template ref to show
  currentTemplateRef: TemplateRef<unknown>;

  // Chart config object
  chartConfig: Highcharts.Options;

  // Series type
  closeSeries: string;

  // series data is not null
  noChartData: boolean = false;

  // Issue type is option with etf
  isETFOption: boolean = false;

  // Used for buy and sell input
  tradeInfo: TradeInfo;

  interlistedExchangeSymbol: SecuritySymbol;
  loading: boolean = true;

  // Config data for chart component
  displayTimeSeries: DisplayTimeSeries = {
    value: 0,
    tabs: QuoteConstants.TABS_DEFAULT,
    selectTimeRange: undefined,
    selectFrequency: undefined,
    selectType: undefined
  };

  seriesData: QuoteTimeSeriesType;

  protected readonly destroy: Subject<void> = new Subject<void>();

  protected constructor(
    protected translateService: TranslateService,
    protected quoteChartConfigService: QuoteChartConfigService,
    protected quoteRestService: QuoteRestService,
    protected securityService: SecurityService,
    protected changeDetectorRef: ChangeDetectorRef) {
  }

  ngAfterViewInit(): void {
    this.refreshQuote();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.symbol !== undefined && !changes.symbol.firstChange && changes.symbol.currentValue !== changes.symbol.previousValue
      && (changes.symbol.currentValue.tickerSymbol !== changes.symbol.previousValue.tickerSymbol
        || changes.symbol.currentValue.market !== changes.symbol.previousValue.market
        || changes.symbol.currentValue.type !== changes.symbol.previousValue.type)) {
      this.interlistedExchangeSymbol = undefined;
      this.refreshQuote();
    }
  }

  /**
   * Destroy - unsubscribe from window size Subscription
   */
  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  /**
   * get active template based on issue type and service response
   *
   * @returns template to be used in template outlet
   */
  getActiveTemplate(): TemplateRef<unknown> | undefined {
    switch (this.template) {
      case QuoteTemplate.NO_PERMISSION:
      case QuoteTemplate.ERROR_MESSAGE:
      case QuoteTemplate.UNKNOWN_SYMBOL:
        return this.templateRefMessage;
      case QuoteTemplate.ISSUE_TYPE:
        return this.getIssueTypeTemplate();
      case QuoteTemplate.FIXED_INCOME:
        return this.templateRefFixedIncome;
      case QuoteTemplate.INVALID_SYMBOL:
        return this.templateRefInvalidSymbol;
      default:
        return undefined;
    }
  }

  /**
   * Handle on tab change event
   * Creates new chartConfig object so that child components
   * can detech changes in chartConfig
   *
   * Refresh chartConfig based on selected tab
   *
   * @param tabId
   */
  onTabChange(tabId: number): void {
    this.displayTimeSeries.value = tabId;

    const type = this.displayTimeSeries.tabs[this.displayTimeSeries.value].type;
    switch (type) {
      case QuoteConstants.CURRENT_TAB_MONTHLY.type:
        this.displayTimeSeriesByRange(QuoteTimeRange.MONTHLY);
        break;
      case QuoteConstants.CURRENT_TAB_3MONTHS.type:
        this.displayTimeSeriesByRange(QuoteTimeRange.THREE_MONTHS);
        break;
      case QuoteConstants.CURRENT_TAB_YEARLY.type:
        this.displayTimeSeriesByRange(QuoteTimeRange.YEARLY);
        break;
      default:
        this.displayDefaultTimeSeries();
        break;
    }
  }

  /**
   * Returns translated close price text
   *
   * @returns
   */
  getClosePrice(): string {
    const plotData = this.quoteChartConfigService.getLinePlotData(this.quote);
    const displayName = 'tdWb.markets.quote.chartConfig.' + plotData.displayName;
    return this.translateService.instant(displayName, { value: plotData.displayValue });
  }

  hideQuoteDetail(): boolean {
    return this.displaySymbolDetail && this.quote?.isHalted;
  }

  getSymbol(): SecuritySymbol {
    return this.interlistedExchangeSymbol ?? this.symbol;
  }

  /**
   * get template based on issue type (overridden in derived class)
   *
   * @returns template to be used in template outlet
   */
  protected getIssueTypeTemplate(): TemplateRef<unknown> | undefined {
    return undefined;
  }

  /**
   * Refresh quote and update chart config
   */
  protected refreshQuote(): void {
    const symbol = this.getSymbol();
    if (symbol !== undefined && symbol.symbolType === SymbolType.FIXED_INCOME && this.hasFixedIncomePermission()) {
      this.template = QuoteTemplate.FIXED_INCOME;
      this.currentTemplateRef = this.getActiveTemplate();
      this.loading = false;
    } else if (!this.hasSymbolInfo(symbol)) {
      this.template = QuoteTemplate.INVALID_SYMBOL;
      this.currentTemplateRef = this.getActiveTemplate();
      this.loading = false;
    } else {
      const quoteParams: QuoteParams = {
        symbol: symbol.symbolType === SymbolType.OPTION ? `${symbol.tickerSymbol} ${symbol.market}` : symbol.tickerSymbol,
        market: this.getMarket(symbol)
      };
      this.quoteRestService.getQuote(quoteParams).pipe(
        takeUntil(this.destroy)
      ).subscribe({
        next: (quote: DetailedQuote): void => {
          this.quote = this.requestQuoteSuccess(quote);
          this.configureChart(quote);
          this.loading = false;
          this.changeDetectorRef.markForCheck();
        },
        error: (errorResponse: Array<BackendError>): void => {
          this.requestQuoteFail(errorResponse);
          this.loading = false;
          this.changeDetectorRef.markForCheck();
        }
      });
    }
  }

  /**
   * Called on rest service time series success
   *
   * Updates chart config
   *
   * @param response
   * @param updateChartSeries
   */
  protected requestTimeSeriesSuccess(response: QuoteTimeSeries, updateChartSeries: unknown): void {
    this.chartConfig = { ...this.chartConfig };
    this.chartConfig.accessibility = {
      description: '#td-wb-quote-chart-a11y-content'
    };
    this.seriesData = response?.ohlcSeries;
    const chartType = this.getChartType();
    const data = QuoteConstants.mapData[chartType](this.seriesData);

    this.noChartData = this.seriesData === undefined || this.seriesData.length === undefined;
    if (this.chartConfig !== undefined &&
      this.chartConfig.series !== undefined &&
      this.chartConfig.series.length > 0
    ) {
      ((this.chartConfig.series[0] as SeriesOptionsType).type) = chartType;
    }

    if (updateChartSeries === undefined) {
      if (this.quote.issueType === 'MF') {
        this.quoteChartConfigService.updateMonthlyChartSeries(0, data, this.chartConfig, this.quote.isPennyStock);
        this.quoteChartConfigService.resetYAxisPlotlines(this.chartConfig);
      } else {
        let open;
        if (this.quote.timeSeriesMarketTime !== undefined) {
          open = this.quote.timeSeriesMarketTime.open;
        }
        this.quoteChartConfigService.updateDailyChartSeries(0, data, this.chartConfig, open, this.quote.isPennyStock);
        this.quoteChartConfigService.refreshClosePlotline(this.quote, this.chartConfig);
        this.adjustYAxisForPlotline(this.seriesData);
      }
      this.quoteChartConfigService.updateChartVolume(this.chartConfig, QuoteConstants.mapData['volume'](this.seriesData));
    } else {
      this.quoteChartConfigService.resetYAxisPlotlines(this.chartConfig);
      if (typeof updateChartSeries === 'function') {
        updateChartSeries(0, data, this.chartConfig, this.quote.isPennyStock, this.quote.timeSeriesMarketTime);
      }
      this.quoteChartConfigService.updateChartVolume(this.chartConfig, QuoteConstants.mapData['volume'](this.seriesData));
    }
  }

  /**
   * Called on rest service success
   *
   * Updates template and quote object
   * Emits a new quote object
   *
   * @param quote
   * @returns quote
   */
  protected requestQuoteSuccess(quote: DetailedQuote): DetailedQuote {
    // TODO: MERGE IT WITH DetailedQuoteComponent CLASS METHOD
    this.template = QuoteTemplate.ISSUE_TYPE;
    this.quote = quote;
    // Set current ref based on template and quote issue type
    this.currentTemplateRef = this.getActiveTemplate();
    this.setTradeInfo(quote);

    this.onQuoteUpdate.emit({ ...quote });
    this.isETFOption = quote.issueType === QuoteIssueType.OPTION && this.quote.underlyingIssueType === QuoteIssueType.ETF;
    this.hideButtons.emit(this.hideQuoteDetail());

    return quote;
  }

  /**
   * Display TimeSeries By Frequency
   * Called by displayTimeSeriesByRange
   * Returns chart update function
   *
   * @param timeRange
   * @returns Chart update function
   */
  protected dailyTimeSeriesFrequency(timeRange: QuoteTimeRange): (...arg: Array<any>) => void {
    let updateCallback: (...arg: Array<any>) => void;
    switch (timeRange) {
      case QuoteTimeRange.FIVE_DAYS:
        updateCallback = this.quoteChartConfigService.updateFiveDaysChartSeries;
        break;
      case QuoteTimeRange.MONTHLY:
        updateCallback = this.quoteChartConfigService.updateMonthlyChartSeries;
        break;
      case QuoteTimeRange.THREE_MONTHS:
        updateCallback = this.quoteChartConfigService.updateThreeMonthChartSeries;
        break;
      case QuoteTimeRange.YEARLY:
        updateCallback = this.quoteChartConfigService.updateYearlyChartSeries;
        break;
      case QuoteTimeRange.THREE_YEARS:
        updateCallback = this.quoteChartConfigService.updateThreeYearsChartSeries;
        break;
      case QuoteTimeRange.FIVE_YEARS:
        updateCallback = this.quoteChartConfigService.updateFiveYearsChartSeries;
        break;
      case QuoteTimeRange.TEN_YEARS:
        updateCallback = this.quoteChartConfigService.updateTenYearsChartSeries;
        break;
      case QuoteTimeRange.TWENTY_YEARS:
        updateCallback = this.quoteChartConfigService.updateTwentyYearsChartSeries;
        break;
      default:
        return undefined;
    }

    return updateCallback.bind(this.quoteChartConfigService);
  }

  /**
   * Display Default Time Series based on issue type
   * Monthly for 'MF' and Daily for others
   */
  protected displayDefaultTimeSeries(): void {
    this.chartConfig = { ...this.chartConfig };
    this.seriesData = this.quote.defaultTimeSeries.closeSeries;
    this.noChartData = this.seriesData === undefined || this.seriesData.length === undefined;
    if (this.chartConfig.series !== undefined && this.chartConfig.series.length > 0) {
      (this.chartConfig.series[0] as SeriesOptionsType).type = this.getChartType();
    }

    if (this.quote.issueType === QuoteIssueType.MF) {
      this.quoteChartConfigService.updateMonthlyChartSeries(0, this.seriesData,
        this.chartConfig, this.quote.isPennyStock);
      this.quoteChartConfigService.resetYAxisPlotlines(this.chartConfig);
    } else {
      this.quoteChartConfigService.updateDailyChartSeries(0, this.seriesData,
        this.chartConfig, this.quote.timeSeriesMarketTime.open, this.quote.isPennyStock);
    }
  }

  /**
   * Update chart config
   * Called on rest service success
   *
   *
   * @param quote
   */
  protected configureChart(quote: DetailedQuote): void {
    this.template = QuoteTemplate.ISSUE_TYPE;
    this.displayTimeSeries.tabs = quote.issueType === QuoteIssueType.MF ?
      QuoteConstants.TABS_MF : QuoteConstants.TABS_DEFAULT;

    this.chartConfig = this.quoteChartConfigService.createStockChartConfig(quote, (quote.issueType !== QuoteIssueType.MF));
    this.displayDefaultTimeSeries();
  }

  protected getSymbolMarket(): string {
    return this.getMarket(this.getSymbol());
  }

  /**
   * Returns market 'CA' or 'US'. Compensate for back end inconsistency in positions call, market for Mutual Funds
   * is returns as 'OTHER', should be 'CA'
   *
   * @returns 'CA' or 'US'
   */
  protected getMarket(symbol: SecuritySymbol = undefined): MarketType {
    if (symbol === undefined) {
      symbol = this.getSymbol();
    }
    if (symbol.symbolType === SymbolType.STOCK) {
      return symbol.market;
    } else {
      return symbol.market === MarketType.US ? MarketType.US : MarketType.CA;
    }
  }

  /**
   * Called on rest service error
   *
   * Updates template and emits new empty quote object
   *
   */
  private requestQuoteFail(errorResponse: Array<BackendError>): void {
    this.template = QuoteTemplate.ERROR_MESSAGE;

    const error = errorResponse !== undefined && errorResponse.length > 0 ? errorResponse[0].code : undefined;
    if (error !== undefined) {
      if (error === QuoteError.AGREEMENT_DECLINED) {
        this.template = QuoteTemplate.NO_PERMISSION;
      }
      if (error === QuoteError.SYMBOL_NOT_FOUND || error === QuoteError.SYMBOL_INVALID) {
        this.template = QuoteTemplate.UNKNOWN_SYMBOL;
      }
    }

    if (this.template === QuoteTemplate.ERROR_MESSAGE) {
      this.quote = {};
    }

    this.onQuoteUpdate.emit({ ...this.quote });
    this.currentTemplateRef = this.getActiveTemplate();

    this.hideButtons.emit(this.displaySymbolDetail);
  }

  /**
   * Returns chart type
   *
   * @returns chart type
   */
  private getChartType(): QuoteChartType {
    if (this.displayTimeSeries !== undefined &&
      this.displayTimeSeries.selectType !== undefined) {
      return this.displayTimeSeries.selectType.type; // whatever is selected
    }
    return QuoteConstants.CHART_TYPE_SELECTOR[0].type;
  }

  /**
   * Adjust YAxis for Plotline
   * Sets min and max for YAxis
   *
   * @param seriesData
   */
  private adjustYAxisForPlotline(seriesData: QuoteTimeSeriesType): void {
    const range = this.adjustYAxisExtremitiesEvent(seriesData);
    this.chartConfig.yAxis[0].min = range[0];
    this.chartConfig.yAxis[0].max = range[1];
  }

  /**
   * Adjust YAxis Extremities Event
   */
  private adjustYAxisExtremitiesEvent(seriesData: QuoteTimeSeriesType): Array<number> {
    let range = [];
    const linePlotData = this.quoteChartConfigService.getLinePlotData(this.quote);
    const plotLineValue = linePlotData.value;
    if (plotLineValue !== undefined && seriesData !== undefined) {
      range = this.quoteChartConfigService.calculateRangeExtremities(plotLineValue, seriesData);
    }
    return range;
  }

  /**
   * Display TimeSeries By Range
   * Called on tab change
   *
   * @param timeRange
   */
  private displayTimeSeriesByRange(timeRange: QuoteTimeRange): void {
    const updateChartSeries = this.dailyTimeSeriesFrequency(timeRange);
    const params: QuoteTimeRangeParams = {
      symbol: this.quote.tickerSymbolForTimeSeries,
      market: this.getSymbolMarket(),
      timeRange
    };
    this.quoteRestService.getTimeSeries(params)
      .pipe(takeUntil(this.destroy))
      .subscribe((timeSeries: unknown): void => {
        this.requestTimeSeriesSuccess(timeSeries, updateChartSeries);
        this.changeDetectorRef.markForCheck();
      });
  }

  /**
   * Set trade info
   * Trade info is used by Buy Sell
   *
   * @param quote
   * @returns
   */
  private setTradeInfo(quote: DetailedQuote): TradeInfo {
    this.tradeInfo = {
      accountId: this.accountId,
      symbol: quote.symbol,
      securityType: quote.tradeableSecurityType,
      market: quote.country,
      quantity: this.quantity
    };
    this.tradeInfoChange.emit(this.tradeInfo);
    return this.tradeInfo;
  }

  private hasFixedIncomePermission(): boolean {
    return this.securityService.hasPermission('FIXED_INCOME');
  }

  private hasSymbolInfo(symbol: SecuritySymbol): boolean {
    return symbol !== undefined &&
      symbol.tickerSymbol !== undefined && symbol.tickerSymbol.length > 0 &&
      symbol.market !== undefined && symbol.market.length > 0 &&
      !(SymbolType.STOCK === symbol.symbolType && MarketType.OTHER === symbol.market);
  }
}
